import { useState, useEffect, useRef, useMemo } from "react";
import { Building2, Mail, Wrench, FileText, Plus, Trash2, RefreshCw, Eye, EyeOff, Sparkles, Loader2, Lock, Users, LogOut, Upload, X, Image, Pencil, Download, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../lib/api";
import logger from "../lib/logger";
import { onUnauthorized } from "../lib/api";
import { CmsEditor } from "../components/admin/CmsEditor";
import { NewsLivePreview } from "../components/admin/NewsLivePreview";
import { LeadSourceTile } from "../components/admin/LeadSourceTile";
import { ZooplaSummaryDialog } from "../components/admin/ZooplaSummaryDialog";
import { labelForSource, KNOWN_SERVICES } from "../lib/leadSources";
import { rowsToCsv, downloadCsv } from "../lib/csv";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  // null = checking session, true = logged in, false = needs to log in
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [news, setNews] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  // Check existing admin session via httpOnly cookie
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.adminMe();
        if (!cancelled) setIsAuthenticated(true);
      } catch {
        if (!cancelled) setIsAuthenticated(false);
      }
    })();
    // Drop to login screen if any API call later returns 401 (cookie expired).
    const unsubscribe = onUnauthorized(() => {
      if (!cancelled) setIsAuthenticated(false);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  // Property form state
  const [propertyForm, setPropertyForm] = useState({
    title: "",
    address: "",
    location: "Batley",
    price: "",
    bedrooms: "",
    bathrooms: "",
    property_type: "House",
    description: "",
    features: "",
    images: "",
    available: true,
  });

  // Edit mode state
  const [editingProperty, setEditingProperty] = useState(null);
  const [zooplaSummaryProperty, setZooplaSummaryProperty] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // News generation form
  const [newsForm, setNewsForm] = useState({
    title: "",
    category: "landlord_tips",
    custom_prompt: "",
  });
  // Live preview state for News > Generate
  const [newsPreview, setNewsPreview] = useState(null);
  // newsPreview shape: { title, excerpt, content, slug, category, status }
  // status ∈ "generating" | "streaming" | "done" | "error"

  // Landlord form
  const [landlordForm, setLandlordForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_ids: "",
  });

  // Review form
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: "",
    rating: 5,
    review_text: "",
    review_date: "",
    source: "Google",
    visible: true,
  });
  const [editingReview, setEditingReview] = useState(null);

  // Maintenance detail view
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [maintenanceDetailOpen, setMaintenanceDetailOpen] = useState(false);

  // Contact detail view
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetailOpen, setContactDetailOpen] = useState(false);
  const [contactSourceFilter, setContactSourceFilter] = useState("all");
  // Secondary filter — only used when contactSourceFilter === "service".
  // Holds either "all" (any additional service) or a specific service slug
  // like "service-section-8-and-possession-notice-service".
  const [contactServiceFilter, setContactServiceFilter] = useState("all");

  // Single source of truth for which contacts the Enquiries table renders.
  // Used by the visible rows AND by the "Export CSV" button so they always match.
  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      if (contactSourceFilter === "all") return true;
      const s = (contact.source || "").toLowerCase();
      if (contactSourceFilter === "direct") return !s;
      if (contactSourceFilter === "sticky") {
        return ["landlords-page", "property-management-page", "lettings-page"].includes(s);
      }
      if (contactSourceFilter === "package") return s.startsWith("package-");
      if (contactSourceFilter === "service") {
        if (!s.startsWith("service-")) return false;
        if (contactServiceFilter === "all") return true;
        return s === contactServiceFilter;
      }
      return s === contactSourceFilter;
    });
  }, [contacts, contactSourceFilter, contactServiceFilter]);

  const handleExportContactsCsv = () => {
    if (filteredContacts.length === 0) {
      toast.error("No enquiries to export with the current filter.");
      return;
    }
    const headers = ["Name", "Email", "Phone", "Type", "Source", "Submitted", "Status", "Message"];
    const rows = filteredContacts.map((c) => [
      c.name || "",
      c.email || "",
      c.phone || "",
      c.submission_type || "",
      labelForSource(c.source),
      c.created_at ? new Date(c.created_at).toISOString() : "",
      c.read ? "Read" : "New",
      c.message || "",
    ]);
    const csv = rowsToCsv(headers, rows);
    const stamp = new Date().toISOString().slice(0, 10);
    const filterTag = contactSourceFilter === "all" ? "all" : contactSourceFilter;
    downloadCsv(`keystone-enquiries-${filterTag}-${stamp}.csv`, csv);
    toast.success(`Exported ${rows.length} enquiry${rows.length === 1 ? "" : "ies"} to CSV.`);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const result = await api.adminLogin(password);
      if (result.success) {
        setIsAuthenticated(true);
        toast.success("Login successful");
        fetchData();
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401) {
        toast.error("Invalid password");
      } else if (status === 500) {
        toast.error(`Server error: ${detail || "missing configuration"}`);
      } else if (!error?.response) {
        // Network / CORS / no response from backend at all
        toast.error("Cannot reach the server. Check your connection or contact support.");
      } else {
        toast.error(`Login failed (${status}): ${detail || "unknown error"}`);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.adminLogout();
    } catch (error) {
      logger.error("Logout request failed:", error);
    }
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, propertiesData, contactsData, maintenanceData, newsData, landlordsData, reviewsData, contentData] = await Promise.all([
        api.getStats(),
        api.getProperties({ available_only: false }),
        api.getContactSubmissions(),
        api.getMaintenanceRequests(),
        api.getNews(null, 20),
        api.getLandlords(),
        api.getReviews(false),
        api.getAllContent(),
      ]);
      setStats(statsData);
      setProperties(propertiesData);
      setContacts(contactsData);
      setMaintenance(maintenanceData);
      setNews(newsData);
      setLandlords(landlordsData);
      setReviews(reviewsData);
      setSiteContent(contentData);
    } catch (error) {
      logger.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleSeedDatabase = async () => {
    try {
      await api.seedDatabase();
      toast.success("Database seeded successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to seed database");
    }
  };

  // Image upload handlers
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const result = await api.uploadMultipleImages(files);
      if (result.uploaded && result.uploaded.length > 0) {
        const newImages = result.uploaded.map(img => `${BACKEND_URL}${img.url}`);
        setUploadedImages(prev => [...prev, ...newImages]);
        toast.success(`${result.uploaded.length} image(s) uploaded successfully!`);
      }
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeUploadedImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      // Combine uploaded images with any manually entered URLs
      const manualImages = propertyForm.images.split(",").map(i => i.trim()).filter(Boolean);
      const allImages = [...uploadedImages, ...manualImages];
      
      const propertyData = {
        ...propertyForm,
        price: parseInt(propertyForm.price),
        bedrooms: parseInt(propertyForm.bedrooms),
        bathrooms: parseInt(propertyForm.bathrooms),
        features: propertyForm.features.split(",").map(f => f.trim()).filter(Boolean),
        images: allImages,
      };
      await api.createProperty(propertyData);
      toast.success("Property created successfully!");
      setPropertyForm({
        title: "",
        address: "",
        location: "Batley",
        price: "",
        bedrooms: "",
        bathrooms: "",
        property_type: "House",
        description: "",
        features: "",
        images: "",
        available: true,
      });
      setUploadedImages([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to create property");
    }
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await api.deleteProperty(id);
      toast.success("Property deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  // Edit property handlers
  const openEditDialog = (property) => {
    setEditingProperty({
      ...property,
      features: property.features?.join(", ") || "",
      images: "", // Manual URL input starts empty
    });
    setUploadedImages(property.images || []);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditingProperty(null);
    setUploadedImages([]);
    setEditDialogOpen(false);
  };

  const handleUpdateProperty = async (e) => {
    e.preventDefault();
    if (!editingProperty) return;
    
    try {
      // Combine uploaded images with any manually entered URLs
      const manualImages = editingProperty.images.split(",").map(i => i.trim()).filter(Boolean);
      const allImages = [...uploadedImages, ...manualImages];
      
      const updateData = {
        title: editingProperty.title,
        address: editingProperty.address,
        location: editingProperty.location,
        price: parseInt(editingProperty.price),
        bedrooms: parseInt(editingProperty.bedrooms),
        bathrooms: parseInt(editingProperty.bathrooms),
        property_type: editingProperty.property_type,
        description: editingProperty.description,
        features: editingProperty.features.split(",").map(f => f.trim()).filter(Boolean),
        images: allImages,
        available: editingProperty.available,
      };
      
      await api.updateProperty(editingProperty.id, updateData);
      toast.success("Property updated successfully!");
      closeEditDialog();
      fetchData();
    } catch (error) {
      toast.error("Failed to update property");
    }
  };

  const generateAndStream = async (formValues, { previousArticleId } = {}) => {
    // Show "generating" skeleton in preview pane immediately
    setNewsPreview({
      title: formValues.title,
      category: formValues.category,
      excerpt: "",
      content: "",
      slug: null,
      status: "generating",
    });
    // If regenerating, delete the old draft first so it doesn't pollute the
    // Published Articles list (best-effort — non-fatal on failure).
    if (previousArticleId) {
      try {
        await api.deleteNewsArticle(previousArticleId);
      } catch (err) {
        logger.error("Failed to delete previous article during regenerate:", err);
      }
    }
    try {
      const article = await api.generateNewsArticle(formValues);
      setNewsPreview({
        title: article.title,
        category: article.category,
        excerpt: article.excerpt,
        content: article.content,
        slug: article.slug,
        // Remember the original prompt so the user can Regenerate without re-typing
        promptParams: formValues,
        articleId: article.id,
        canRegenerate: true,
        status: "streaming",
        onComplete: () => {
          setNewsPreview((p) => (p ? { ...p, status: "done" } : p));
          toast.success("Article saved");
        },
      });
      toast.success("Article generated — review it on the right");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      setNewsPreview((p) => (p ? {
        ...p,
        status: "error",
        error: detail,
        promptParams: formValues,
        canRegenerate: true,
      } : null));
      toast.error("Failed to generate article");
    }
  };

  const handleGenerateNews = async (e) => {
    e.preventDefault();
    if (!newsForm.title) {
      toast.error("Please enter an article title");
      return;
    }
    setGenerating(true);
    try {
      await generateAndStream(newsForm);
      // Clear form only on the FIRST generation — leave the form alone if user is
      // iterating via Regenerate so they can tweak instructions if they want.
      setNewsForm({ title: "", category: "landlord_tips", custom_prompt: "" });
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateNews = async () => {
    if (!newsPreview?.promptParams) return;
    setGenerating(true);
    try {
      await generateAndStream(newsPreview.promptParams, {
        previousArticleId: newsPreview.articleId,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    try {
      await api.deleteNewsArticle(id);
      toast.success("Article deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleUpdateMaintenanceStatus = async (id, status) => {
    try {
      await api.updateMaintenanceStatus(id, status);
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleMarkContactRead = async (id) => {
    try {
      await api.markSubmissionRead(id);
      toast.success("Marked as read");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleCreateLandlord = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: landlordForm.name,
        email: landlordForm.email,
        phone: landlordForm.phone || null,
        property_ids: landlordForm.property_ids.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.createLandlord(data);
      toast.success("Landlord created! Access code has been generated.");
      setLandlordForm({ name: "", email: "", phone: "", property_ids: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create landlord");
    }
  };

  const handleDeleteLandlord = async (id) => {
    if (!window.confirm("Are you sure you want to delete this landlord?")) return;
    try {
      await api.deleteLandlord(id);
      toast.success("Landlord deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete landlord");
    }
  };

  // Review handlers
  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      await api.createReview(reviewForm);
      toast.success("Review added successfully");
      setReviewForm({
        reviewer_name: "",
        rating: 5,
        review_text: "",
        review_date: "",
        source: "Google",
        visible: true,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add review");
    }
  };

  const handleUpdateReview = async (e) => {
    e.preventDefault();
    if (!editingReview) return;
    try {
      await api.updateReview(editingReview.id, reviewForm);
      toast.success("Review updated");
      setEditingReview(null);
      setReviewForm({
        reviewer_name: "",
        rating: 5,
        review_text: "",
        review_date: "",
        source: "Google",
        visible: true,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      await api.deleteReview(id);
      toast.success("Review deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const startEditReview = (review) => {
    setEditingReview(review);
    setReviewForm({
      reviewer_name: review.reviewer_name,
      rating: review.rating,
      review_text: review.review_text,
      review_date: review.review_date,
      source: review.source,
      visible: review.visible,
    });
  };

  // Content handlers
  const handleUpdateContent = async (section, content) => {
    try {
      await api.updateContentSection(section, content);
      toast.success("Content updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update content");
    }
  };

  const handleSeedDefaultContent = async () => {
    try {
      await api.seedDefaultContent();
      toast.success("Default content loaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to seed content");
    }
  };

  // CMS Image Upload Handler
  const handleCMSImageUpload = async (e, section, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Uploading image...");
      const response = await fetch(`${BACKEND_URL}/api/upload/content`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (!response.ok) throw new Error("Upload failed");
      
      const data = await response.json();
      toast.dismiss();
      toast.success("Image uploaded successfully");
      
      // Update the content with the new image URL
      setSiteContent({
        ...siteContent,
        [section]: { ...siteContent[section], [field]: data.url }
      });
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to upload image");
      logger.error(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Initial session check — render nothing while we determine auth state to
  // avoid flashing the login form for already-authenticated visitors.
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4" data-testid="admin-login-page">
        <Card className="w-full max-w-md border-slate-200">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-navy" />
            </div>
            <CardTitle className="text-2xl font-heading">Admin Login</CardTitle>
            <p className="text-slate-500 text-sm">Enter your password to access the dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-navy hover:bg-navy-light"
                disabled={loginLoading}
                data-testid="admin-login-btn"
              >
                {loginLoading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-navy border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-navy">Admin Dashboard</h1>
            <p className="text-slate-600">Manage properties, enquiries, and content</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchData} variant="outline" className="border-slate-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleSeedDatabase} variant="outline" className="border-slate-300" data-testid="admin-seed-btn">
              Seed Data
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-bold text-navy">{stats.available_properties}</div>
                    <div className="text-sm text-slate-500">Properties</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-bold text-navy">{stats.unread_submissions}</div>
                    <div className="text-sm text-slate-500">Enquiries</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-bold text-navy">{stats.maintenance_pending}</div>
                    <div className="text-sm text-slate-500">Maintenance</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-bold text-navy">{news.length}</div>
                    <div className="text-sm text-slate-500">Articles</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-bold text-navy">{landlords.length}</div>
                    <div className="text-sm text-slate-500">Landlords</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leads-by-source weekly tile */}
        <div className="mb-8">
          <LeadSourceTile contacts={contacts} />
        </div>

        {/* Main Content */}
        <Tabs defaultValue="properties">
          <TabsList className="bg-white border border-slate-200 p-1 mb-6 flex-wrap">
            <TabsTrigger value="properties" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Properties
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Enquiries
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Maintenance
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Reviews
            </TabsTrigger>
            <TabsTrigger value="landlords" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Landlords
            </TabsTrigger>
            <TabsTrigger value="news" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              News
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              Website Content
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Properties</CardTitle>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-navy hover:bg-navy-light" data-testid="add-property-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Property
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New Property</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProperty} className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Title *</label>
                        <Input
                          value={propertyForm.title}
                          onChange={(e) => setPropertyForm({ ...propertyForm, title: e.target.value })}
                          placeholder="e.g., Modern 3 Bed House in Batley"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Address *</label>
                        <Input
                          value={propertyForm.address}
                          onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                          placeholder="Full address"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Location *</label>
                          <Select value={propertyForm.location} onValueChange={(v) => setPropertyForm({ ...propertyForm, location: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Batley">Batley</SelectItem>
                              <SelectItem value="Beeston">Beeston</SelectItem>
                              <SelectItem value="Bradford">Bradford</SelectItem>
                              <SelectItem value="Dewsbury">Dewsbury</SelectItem>
                              <SelectItem value="Harehills">Harehills</SelectItem>
                              <SelectItem value="Heckmondwike">Heckmondwike</SelectItem>
                              <SelectItem value="Huddersfield">Huddersfield</SelectItem>
                              <SelectItem value="Hyde Park">Hyde Park</SelectItem>
                              <SelectItem value="Leeds">Leeds</SelectItem>
                              <SelectItem value="Mirfield">Mirfield</SelectItem>
                              <SelectItem value="Morley">Morley</SelectItem>
                              <SelectItem value="Thornhill Lees">Thornhill Lees</SelectItem>
                              <SelectItem value="Tingley">Tingley</SelectItem>
                              <SelectItem value="Wakefield">Wakefield</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Property Type *</label>
                          <Select value={propertyForm.property_type} onValueChange={(v) => setPropertyForm({ ...propertyForm, property_type: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="House">House</SelectItem>
                              <SelectItem value="Apartment">Apartment</SelectItem>
                              <SelectItem value="Flat">Flat</SelectItem>
                              <SelectItem value="Studio">Studio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Price (£/month) *</label>
                          <Input
                            type="number"
                            value={propertyForm.price}
                            onChange={(e) => setPropertyForm({ ...propertyForm, price: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Bedrooms *</label>
                          <Input
                            type="number"
                            value={propertyForm.bedrooms}
                            onChange={(e) => setPropertyForm({ ...propertyForm, bedrooms: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Bathrooms *</label>
                          <Input
                            type="number"
                            value={propertyForm.bathrooms}
                            onChange={(e) => setPropertyForm({ ...propertyForm, bathrooms: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Description *</label>
                        <Textarea
                          value={propertyForm.description}
                          onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                          rows={4}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Features (comma-separated)</label>
                        <Input
                          value={propertyForm.features}
                          onChange={(e) => setPropertyForm({ ...propertyForm, features: e.target.value })}
                          placeholder="Garden, Parking, Gas Central Heating"
                        />
                      </div>
                      
                      {/* Image Upload Section */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Property Images</label>
                        
                        {/* Upload Button */}
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-navy transition-colors">
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            id="image-upload"
                          />
                          <label htmlFor="image-upload" className="cursor-pointer">
                            {uploading ? (
                              <div className="flex flex-col items-center">
                                <Loader2 className="w-8 h-8 text-navy animate-spin mb-2" />
                                <span className="text-slate-600">Uploading...</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                <span className="text-navy font-medium">Click to upload images</span>
                                <span className="text-slate-500 text-sm mt-1">JPG, PNG, WebP up to 10MB each</span>
                              </div>
                            )}
                          </label>
                        </div>

                        {/* Uploaded Images Preview */}
                        {uploadedImages.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-slate-600 mb-2">{uploadedImages.length} image(s) uploaded:</p>
                            <div className="grid grid-cols-4 gap-2">
                              {uploadedImages.map((url, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={url}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-lg border border-slate-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeUploadedImage(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Optional: Manual URL input */}
                        <div className="mt-4">
                          <label className="block text-xs text-slate-500 mb-1">Or add image URLs manually (comma-separated)</label>
                          <Input
                            value={propertyForm.images}
                            onChange={(e) => setPropertyForm({ ...propertyForm, images: e.target.value })}
                            placeholder="https://example.com/image1.jpg"
                          />
                        </div>
                      </div>

                      <Button type="submit" className="w-full bg-navy hover:bg-navy-light">
                        Create Property
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {properties.map((property) => (
                      <TableRow key={property.id}>
                        <TableCell>
                          <div className="font-medium">{property.title}</div>
                          <div className="text-sm text-slate-500">{property.bedrooms} bed, {property.bathrooms} bath</div>
                        </TableCell>
                        <TableCell>{property.location}</TableCell>
                        <TableCell>{formatPrice(property.price)}/mo</TableCell>
                        <TableCell>
                          <Badge variant={property.available ? "default" : "secondary"} className={property.available ? "bg-green-100 text-green-800" : ""}>
                            {property.available ? "Available" : "Let"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-navy hover:text-navy-light hover:bg-navy/5"
                              onClick={() => openEditDialog(property)}
                              title="Edit property"
                              data-testid={`edit-property-${property.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gold hover:text-gold-light hover:bg-gold/5"
                              onClick={() => setZooplaSummaryProperty(property)}
                              title="Zoopla-Ready Summary"
                              data-testid={`zoopla-summary-${property.id}`}
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteProperty(property.id)}
                              title="Delete property"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {properties.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No properties found. Click "Add Property" to create one.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Edit Property Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Property</DialogTitle>
                </DialogHeader>
                {editingProperty && (
                  <form onSubmit={handleUpdateProperty} className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title *</label>
                      <Input
                        value={editingProperty.title}
                        onChange={(e) => setEditingProperty({ ...editingProperty, title: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Address *</label>
                      <Input
                        value={editingProperty.address}
                        onChange={(e) => setEditingProperty({ ...editingProperty, address: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Location *</label>
                        <Select value={editingProperty.location} onValueChange={(v) => setEditingProperty({ ...editingProperty, location: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Batley">Batley</SelectItem>
                            <SelectItem value="Beeston">Beeston</SelectItem>
                            <SelectItem value="Bradford">Bradford</SelectItem>
                            <SelectItem value="Dewsbury">Dewsbury</SelectItem>
                            <SelectItem value="Harehills">Harehills</SelectItem>
                            <SelectItem value="Heckmondwike">Heckmondwike</SelectItem>
                            <SelectItem value="Huddersfield">Huddersfield</SelectItem>
                            <SelectItem value="Hyde Park">Hyde Park</SelectItem>
                            <SelectItem value="Leeds">Leeds</SelectItem>
                            <SelectItem value="Mirfield">Mirfield</SelectItem>
                            <SelectItem value="Morley">Morley</SelectItem>
                            <SelectItem value="Thornhill Lees">Thornhill Lees</SelectItem>
                            <SelectItem value="Tingley">Tingley</SelectItem>
                            <SelectItem value="Wakefield">Wakefield</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Property Type *</label>
                        <Select value={editingProperty.property_type} onValueChange={(v) => setEditingProperty({ ...editingProperty, property_type: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="House">House</SelectItem>
                            <SelectItem value="Apartment">Apartment</SelectItem>
                            <SelectItem value="Flat">Flat</SelectItem>
                            <SelectItem value="Studio">Studio</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Price (£/month) *</label>
                        <Input
                          type="number"
                          value={editingProperty.price}
                          onChange={(e) => setEditingProperty({ ...editingProperty, price: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Bedrooms *</label>
                        <Input
                          type="number"
                          value={editingProperty.bedrooms}
                          onChange={(e) => setEditingProperty({ ...editingProperty, bedrooms: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Bathrooms *</label>
                        <Input
                          type="number"
                          value={editingProperty.bathrooms}
                          onChange={(e) => setEditingProperty({ ...editingProperty, bathrooms: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <Select value={editingProperty.available ? "available" : "let"} onValueChange={(v) => setEditingProperty({ ...editingProperty, available: v === "available" })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available</SelectItem>
                          <SelectItem value="let">Let</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description *</label>
                      <Textarea
                        value={editingProperty.description}
                        onChange={(e) => setEditingProperty({ ...editingProperty, description: e.target.value })}
                        rows={4}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Features (comma-separated)</label>
                      <Input
                        value={editingProperty.features}
                        onChange={(e) => setEditingProperty({ ...editingProperty, features: e.target.value })}
                        placeholder="Garden, Parking, Gas Central Heating"
                      />
                    </div>
                    
                    {/* Image Management Section */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Property Images</label>
                      
                      {/* Current Images */}
                      {uploadedImages.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm text-slate-600 mb-2">Current images ({uploadedImages.length}):</p>
                          <div className="grid grid-cols-4 gap-2">
                            {uploadedImages.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Image ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg border border-slate-200"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeUploadedImage(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Upload More Images */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-navy transition-colors">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="edit-image-upload"
                        />
                        <label htmlFor="edit-image-upload" className="cursor-pointer">
                          {uploading ? (
                            <div className="flex items-center justify-center gap-2">
                              <Loader2 className="w-5 h-5 text-navy animate-spin" />
                              <span className="text-slate-600">Uploading...</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="text-navy font-medium">Add more images</span>
                            </div>
                          )}
                        </label>
                      </div>

                      {/* Manual URL input */}
                      <div className="mt-3">
                        <label className="block text-xs text-slate-500 mb-1">Or add image URLs (comma-separated)</label>
                        <Input
                          value={editingProperty.images}
                          onChange={(e) => setEditingProperty({ ...editingProperty, images: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeEditDialog} className="flex-1">
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 bg-navy hover:bg-navy-light">
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
                <CardTitle>Contact Enquiries</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-sm text-slate-500 whitespace-nowrap">Filter source:</label>
                  <Select
                    value={contactSourceFilter}
                    onValueChange={(value) => {
                      setContactSourceFilter(value);
                      // Reset sub-filter whenever the primary changes.
                      setContactServiceFilter("all");
                    }}
                  >
                    <SelectTrigger className="w-[220px]" data-testid="contact-source-filter">
                      <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sources</SelectItem>
                      <SelectItem value="direct">Direct (no source)</SelectItem>
                      <SelectItem value="sticky">Sticky CTA (any)</SelectItem>
                      <SelectItem value="landlords-page">Landlords (sticky)</SelectItem>
                      <SelectItem value="property-management-page">Property Mgmt (sticky)</SelectItem>
                      <SelectItem value="lettings-page">Lettings (sticky)</SelectItem>
                      <SelectItem value="package">Packages (any)</SelectItem>
                      <SelectItem value="service">Additional Services</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Secondary per-service filter — only when Additional Services is selected */}
                  {contactSourceFilter === "service" && (
                    <Select value={contactServiceFilter} onValueChange={setContactServiceFilter}>
                      <SelectTrigger className="w-[240px]" data-testid="contact-service-filter">
                        <SelectValue placeholder="Any service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any service</SelectItem>
                        {KNOWN_SERVICES.map((svc) => (
                          <SelectItem key={svc.id} value={svc.id}>
                            {svc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportContactsCsv}
                    disabled={filteredContacts.length === 0}
                    data-testid="export-contacts-csv"
                    className="border-navy/20 text-navy hover:bg-navy/5"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{contact.submission_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={contact.source ? "border-gold/40 text-navy" : "text-slate-400"}
                            data-testid={`contact-source-${contact.id}`}
                          >
                            {labelForSource(contact.source)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(contact.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant={contact.read ? "secondary" : "default"} className={!contact.read ? "bg-gold text-navy" : ""}>
                            {contact.read ? "Read" : "New"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedContact(contact);
                              setContactDetailOpen(true);
                              if (!contact.read) {
                                handleMarkContactRead(contact.id);
                              }
                            }}
                            data-testid={`view-contact-${contact.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredContacts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          {contacts.length === 0
                            ? "No contact submissions yet."
                            : "No enquiries match the current filter."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Contact Detail Dialog */}
            <Dialog open={contactDetailOpen} onOpenChange={setContactDetailOpen}>
              <DialogContent
                className="max-w-lg max-h-[90vh] overflow-y-auto"
                data-testid="contact-detail-dialog"
              >
                <DialogHeader>
                  <DialogTitle>Contact Enquiry Details</DialogTitle>
                </DialogHeader>
                {selectedContact && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Name</h4>
                        <p className="font-medium">{selectedContact.name}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Type</h4>
                        <Badge variant="secondary">{selectedContact.submission_type}</Badge>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Source</h4>
                      <Badge
                        variant="outline"
                        className={selectedContact.source ? "border-gold/40 text-navy" : "text-slate-400"}
                      >
                        {labelForSource(selectedContact.source)}
                      </Badge>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Email</h4>
                      <a href={`mailto:${selectedContact.email}`} className="text-navy hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                    {selectedContact.phone && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Phone</h4>
                        <a href={`tel:${selectedContact.phone}`} className="text-navy hover:underline">
                          {selectedContact.phone}
                        </a>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-slate-500">Message</h4>
                        <span className="text-xs text-slate-400" data-testid="contact-message-length">
                          {(selectedContact.message || "").length.toLocaleString()} characters
                        </span>
                      </div>
                      <div
                        className="bg-slate-50 border border-slate-200 p-4 rounded-lg whitespace-pre-wrap break-words text-slate-800 leading-relaxed max-h-96 overflow-y-auto text-sm"
                        data-testid="contact-message-body"
                      >
                        {selectedContact.message}
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Submitted</h4>
                      <p>{formatDate(selectedContact.created_at)}</p>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Maintenance Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Urgency</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenance.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="font-medium">{request.tenant_name}</div>
                          <div className="text-sm text-slate-500">{request.tenant_email}</div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{request.property_address}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={
                              request.urgency === "emergency" ? "bg-red-100 text-red-800" :
                              request.urgency === "urgent" ? "bg-orange-100 text-orange-800" :
                              "bg-blue-100 text-blue-800"
                            }
                          >
                            {request.urgency}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(request.created_at)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={
                              request.status === "resolved" ? "bg-green-100 text-green-800" :
                              request.status === "in_progress" ? "bg-yellow-100 text-yellow-800" :
                              "bg-slate-100 text-slate-800"
                            }
                          >
                            {request.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedMaintenance(request);
                                setMaintenanceDetailOpen(true);
                              }}
                              data-testid={`view-maintenance-${request.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Select value={request.status} onValueChange={(v) => handleUpdateMaintenanceStatus(request.id, v)}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {maintenance.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No maintenance requests yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Maintenance Detail Dialog */}
            <Dialog open={maintenanceDetailOpen} onOpenChange={setMaintenanceDetailOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Maintenance Request Details</DialogTitle>
                </DialogHeader>
                {selectedMaintenance && (
                  <div className="space-y-6">
                    {/* Tenant Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Tenant Name</h4>
                        <p className="font-medium">{selectedMaintenance.tenant_name}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Email</h4>
                        <a href={`mailto:${selectedMaintenance.tenant_email}`} className="text-navy hover:underline">
                          {selectedMaintenance.tenant_email}
                        </a>
                      </div>
                      {selectedMaintenance.tenant_phone && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-500 mb-1">Phone</h4>
                          <a href={`tel:${selectedMaintenance.tenant_phone}`} className="text-navy hover:underline">
                            {selectedMaintenance.tenant_phone}
                          </a>
                        </div>
                      )}
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Urgency</h4>
                        <Badge 
                          variant="secondary"
                          className={
                            selectedMaintenance.urgency === "emergency" ? "bg-red-100 text-red-800" :
                            selectedMaintenance.urgency === "urgent" ? "bg-orange-100 text-orange-800" :
                            "bg-blue-100 text-blue-800"
                          }
                        >
                          {selectedMaintenance.urgency}
                        </Badge>
                      </div>
                    </div>

                    {/* Property Address */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Property Address</h4>
                      <p className="font-medium">{selectedMaintenance.property_address}</p>
                    </div>

                    {/* Issue Description */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-1">Issue Description</h4>
                      <p className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">{selectedMaintenance.issue_description}</p>
                    </div>

                    {/* Attachments/Images */}
                    {selectedMaintenance.attachments && selectedMaintenance.attachments.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">
                          Attachments ({selectedMaintenance.attachments.length})
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {selectedMaintenance.attachments.map((url, index) => {
                            const isVideo = url.match(/\.(mp4|mov|avi|webm)$/i);
                            return (
                              <div key={index} className="relative">
                                {isVideo ? (
                                  <video 
                                    src={url} 
                                    controls 
                                    className="w-full rounded-lg border"
                                  />
                                ) : (
                                  <a href={url} target="_blank" rel="noopener noreferrer">
                                    <img 
                                      src={url} 
                                      alt={`Attachment ${index + 1}`}
                                      className="w-full h-48 object-cover rounded-lg border hover:opacity-90 transition-opacity cursor-pointer"
                                    />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Status & Date */}
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Status</h4>
                        <Select 
                          value={selectedMaintenance.status} 
                          onValueChange={(v) => {
                            handleUpdateMaintenanceStatus(selectedMaintenance.id, v);
                            setSelectedMaintenance({...selectedMaintenance, status: v});
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-1">Submitted</h4>
                        <p>{formatDate(selectedMaintenance.created_at)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Landlords Tab */}
          <TabsContent value="landlords">
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-navy" />
                    Add Landlord
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateLandlord} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name *</label>
                      <Input
                        value={landlordForm.name}
                        onChange={(e) => setLandlordForm({ ...landlordForm, name: e.target.value })}
                        placeholder="Landlord name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <Input
                        type="email"
                        value={landlordForm.email}
                        onChange={(e) => setLandlordForm({ ...landlordForm, email: e.target.value })}
                        placeholder="landlord@email.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone</label>
                      <Input
                        value={landlordForm.phone}
                        onChange={(e) => setLandlordForm({ ...landlordForm, phone: e.target.value })}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Property IDs (comma-separated)</label>
                      <Input
                        value={landlordForm.property_ids}
                        onChange={(e) => setLandlordForm({ ...landlordForm, property_ids: e.target.value })}
                        placeholder="property-id-1, property-id-2"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-navy hover:bg-navy-light">
                      Create Landlord
                    </Button>
                  </form>
                  <p className="text-xs text-slate-500 mt-4">
                    An access code will be generated automatically. Share this with the landlord to access their portal.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle>Registered Landlords</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Access Code</TableHead>
                        <TableHead>Properties</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {landlords.map((landlord) => (
                        <TableRow key={landlord.id}>
                          <TableCell className="font-medium">{landlord.name}</TableCell>
                          <TableCell>{landlord.email}</TableCell>
                          <TableCell>
                            <code className="bg-slate-100 px-2 py-1 rounded text-sm">{landlord.access_code}</code>
                          </TableCell>
                          <TableCell>{landlord.property_ids?.length || 0}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteLandlord(landlord.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {landlords.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No landlords registered yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            {/* Top: split-screen — form (left) + live preview (right) */}
            <div className="grid lg:grid-cols-5 gap-6 mb-6">
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-gold" />
                    Generate Article
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGenerateNews} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Article Title *</label>
                      <Input
                        value={newsForm.title}
                        onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                        placeholder="e.g., How to Reduce Void Periods"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <Select value={newsForm.category} onValueChange={(v) => setNewsForm({ ...newsForm, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="landlord_tips">Landlord Tips</SelectItem>
                          <SelectItem value="tenant_advice">Tenant Advice</SelectItem>
                          <SelectItem value="market_insights">Market Insights</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Custom Instructions (optional)</label>
                      <Textarea
                        value={newsForm.custom_prompt}
                        onChange={(e) => setNewsForm({ ...newsForm, custom_prompt: e.target.value })}
                        placeholder="Any specific points to cover..."
                        rows={3}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-navy hover:bg-navy-light"
                      disabled={generating}
                      data-testid="generate-article-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Article
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Live preview pane */}
              <div className="lg:col-span-3">
                <NewsLivePreview
                  preview={newsPreview}
                  onDismiss={() => setNewsPreview(null)}
                  onRegenerate={handleRegenerateNews}
                  busy={generating}
                />
              </div>
            </div>

            {/* Bottom: published articles list */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Published Articles</CardTitle>
              </CardHeader>
              <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {news.map((article) => (
                        <TableRow key={article.id}>
                          <TableCell className="font-medium max-w-[300px] truncate">
                            {article.title}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{article.category}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(article.created_at)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteNews(article.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {news.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                            No articles yet. Generate one using the form.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Add/Edit Review Form */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>{editingReview ? "Edit Review" : "Add Google Review"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={editingReview ? handleUpdateReview : handleCreateReview} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Reviewer Name *</label>
                      <Input
                        value={reviewForm.reviewer_name}
                        onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                        placeholder="John Smith"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Rating *</label>
                      <Select value={String(reviewForm.rating)} onValueChange={(v) => setReviewForm({ ...reviewForm, rating: parseInt(v) })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">⭐⭐⭐⭐⭐ 5 Stars</SelectItem>
                          <SelectItem value="4">⭐⭐⭐⭐ 4 Stars</SelectItem>
                          <SelectItem value="3">⭐⭐⭐ 3 Stars</SelectItem>
                          <SelectItem value="2">⭐⭐ 2 Stars</SelectItem>
                          <SelectItem value="1">⭐ 1 Star</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Review Text *</label>
                      <Textarea
                        value={reviewForm.review_text}
                        onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                        placeholder="The review content..."
                        rows={4}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Review Date</label>
                      <Input
                        value={reviewForm.review_date}
                        onChange={(e) => setReviewForm({ ...reviewForm, review_date: e.target.value })}
                        placeholder="e.g., 2 months ago, January 2025"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source</label>
                      <Select value={reviewForm.source} onValueChange={(v) => setReviewForm({ ...reviewForm, source: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Google">Google</SelectItem>
                          <SelectItem value="Facebook">Facebook</SelectItem>
                          <SelectItem value="Trustpilot">Trustpilot</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="visible"
                        checked={reviewForm.visible}
                        onChange={(e) => setReviewForm({ ...reviewForm, visible: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label htmlFor="visible" className="text-sm">Visible on website</label>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1 bg-navy hover:bg-navy-light">
                        {editingReview ? "Update Review" : "Add Manual Review"}
                      </Button>
                      {editingReview && (
                        <Button type="button" variant="outline" onClick={() => {
                          setEditingReview(null);
                          setReviewForm({ reviewer_name: "", rating: 5, review_text: "", review_date: "", source: "Google", visible: true });
                        }}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Reviews List */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Manage Reviews ({reviews.length})</CardTitle>
                  <Button 
                    onClick={async () => {
                      try {
                        toast.loading("Syncing reviews from Google...");
                        const result = await api.syncGoogleReviews();
                        toast.dismiss();
                        toast.success(`Synced! ${result.message}`);
                        fetchData();
                      } catch (error) {
                        toast.dismiss();
                        toast.error("Failed to sync reviews");
                      }
                    }}
                    variant="outline"
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Sync from Google
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Toggle visibility to choose which reviews appear on your website. Only reviews marked as "Visible" will be shown.
                  </p>
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className={`border rounded-lg p-4 ${!review.visible ? 'opacity-60 bg-slate-50' : ''}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-3">
                            {review.profile_photo && (
                              <img src={review.profile_photo} alt="" className="w-10 h-10 rounded-full" />
                            )}
                            <div>
                              <div className="font-medium">{review.reviewer_name}</div>
                              <div className="text-sm text-slate-500">{review.source} • {review.review_date}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-gold text-sm">{"⭐".repeat(review.rating)}</div>
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm mb-3">{review.review_text}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => startEditReview(review)}>
                              <Pencil className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteReview(review.id)}>
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>
                          <Button
                            variant={review.visible ? "default" : "outline"}
                            size="sm"
                            onClick={async () => {
                              try {
                                await api.toggleReviewVisibility(review.id, !review.visible);
                                toast.success(review.visible ? "Review hidden" : "Review now visible");
                                fetchData();
                              } catch (error) {
                                toast.error("Failed to update visibility");
                              }
                            }}
                            className={review.visible ? "bg-green-600 hover:bg-green-700" : ""}
                          >
                            {review.visible ? (
                              <><Eye className="w-4 h-4 mr-1" /> Visible</>
                            ) : (
                              <><EyeOff className="w-4 h-4 mr-1" /> Hidden</>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                    {reviews.length === 0 && (
                      <p className="text-center text-slate-500 py-8">
                        No reviews yet. Click "Sync from Google" to import your Google reviews, or add a manual review above.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Website Content Tab */}
          <TabsContent value="content">
            <CmsEditor
              siteContent={siteContent}
              setSiteContent={setSiteContent}
              handleUpdateContent={handleUpdateContent}
              handleSeedDefaultContent={handleSeedDefaultContent}
              handleCMSImageUpload={handleCMSImageUpload}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Zoopla-Ready Summary Dialog — mounted at page level so it works
          across all admin tabs and doesn't get affected by tab state. */}
      <ZooplaSummaryDialog
        open={!!zooplaSummaryProperty}
        onOpenChange={(open) => !open && setZooplaSummaryProperty(null)}
        property={zooplaSummaryProperty}
      />
    </div>
  );
};

export default AdminPage;
